import { useCallback, useState } from 'react';
import { Map, APIProvider, Marker, useMap } from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from 'src/config';
import { LucideAlertCircle, LucideLocateFixed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { formatPhone } from 'src/utils/format-phone';
import { DumpsterRectangleOverlay } from '../../maps/DumpsterRectangleOverlay';
import { feetToMeters } from 'src/utils/map-calculations';

interface OrderDeliveryLocationMapProps {
  orderId: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    lat?: number;
    long?: number;
  };
  driver?: {
    name: {
      first: string;
      last: string;
    };
    phone: string;
  } | null;
  containerAction: 'dropoff' | 'pickup';
  pinLocation?: { lat: number; lng: number; rotation?: number; zoom?: number } | null;
}

// Default dumpster dimensions (21'11" L x 7'0" W)
const DEFAULT_DUMPSTER_LENGTH_FEET = 21 + 11 / 12;
const DEFAULT_DUMPSTER_WIDTH_FEET = 7;
// Cap at 19 to match customer-side satellite/hybrid mode effective max.
// Google Maps satellite tiles above zoom 19 often trigger 45-degree imagery
// or show blurry interpolated tiles, so both sides stay at <=19.
const DEFAULT_ADMIN_MAP_ZOOM = 19;

const ResetToSavedPinButton = ({
  pinLocation,
}: {
  pinLocation: { lat: number; lng: number; rotation?: number; zoom?: number };
}) => {
  const map = useMap();

  const { lat, lng, zoom } = pinLocation;
  const handleReset = useCallback(() => {
    if (!map) return;

    map.panTo({ lat, lng });
    map.setZoom(zoom ?? DEFAULT_ADMIN_MAP_ZOOM);
    map.setTilt(0);
  }, [map, lat, lng, zoom]);

  return (
    <Button
      type="button"
      size="icon"
      variant="secondary"
      onClick={handleReset}
      className="absolute left-3 top-3 z-10 h-8 w-8 rounded-md border border-gray-200 bg-white shadow-sm"
      title="Reset to saved pin location"
    >
      <LucideLocateFixed className="h-4 w-4" />
    </Button>
  );
};

export const OrderDeliveryLocationMap = ({
  orderId,
  address,
  driver,
  containerAction,
  pinLocation,
}: OrderDeliveryLocationMapProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { successToast, errorToast } = useToast();
  const defaultCenter = pinLocation || { lat: address?.lat || 0, lng: address?.long || 0 };
  const defaultZoom = pinLocation?.zoom ?? DEFAULT_ADMIN_MAP_ZOOM;

  // Calculate dumpster dimensions
  const dumpsterLengthMeters = feetToMeters(DEFAULT_DUMPSTER_LENGTH_FEET);
  const dumpsterWidthMeters = feetToMeters(DEFAULT_DUMPSTER_WIDTH_FEET);
  const rotation = pinLocation?.rotation || 0;

  const utils = trpc.useUtils();
  const { mutate: sendLocationToDriver, isPending: isSending } =
    trpc.admin.order.adminSendLocationToDriver.useMutation({
      onSuccess: () => {
        successToast({
          title: 'Location Sent',
          description: `Delivery location has been sent to ${driver?.name.first} ${driver?.name.last}`,
        });
        setShowConfirmDialog(false);
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
      },
      onError: (error) => {
        errorToast({
          title: 'Failed to Send Location',
          description: error.message || 'Unable to send location to driver. Please try again.',
        });
        setShowConfirmDialog(false);
      },
    });

  const handleSendToDriver = () => {
    sendLocationToDriver({
      orderId,
      containerAction,
    });
  };

  // If no lat/long coordinates, show message
  if (!pinLocation || pinLocation.lat === 0 || pinLocation.lng === 0) {
    return (
      <div className="card flex flex-col w-full p-4 mt-2">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="delivery-location" className="border-none">
            <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:no-underline">
              Delivery Location
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-row gap-3 rounded-lg bg-gray-50 border border-gray-200 p-4 items-start w-full">
                <LucideAlertCircle className="w-5 h-5 text-gray-500" />
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-gray-700">
                    Pin location not available for this order. Customer may have placed the order
                    before the pin location feature was implemented.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY || ''}>
      <div className="card flex flex-col w-full p-4 mt-2 items-center">
        <Accordion type="single" collapsible className="w-full" defaultValue="delivery-location">
          <AccordionItem value="delivery-location" className="border-none">
            <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:no-underline">
              Delivery Location
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-4 items-center">
                <div className="border border-gray-200 rounded-lg overflow-hidden w-full">
                  <div className="relative h-64 sm:h-96">
                    <Map
                      style={{ width: '100%', height: '100%' }}
                      defaultCenter={defaultCenter}
                      defaultZoom={defaultZoom}
                      defaultTilt={0}
                      gestureHandling="greedy"
                      disableDefaultUI={true}
                      zoomControl={true}
                      mapTypeControl={true}
                      streetViewControl={false}
                      fullscreenControl={false}
                      rotateControl={false}
                      mapTypeId="hybrid"
                      tilt={0}
                      heading={0}
                      mapTypeControlOptions={{
                        mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
                        position: 3, // TOP_RIGHT
                      }}
                      zoomControlOptions={{
                        position: 8, // RIGHT_CENTER
                      }}
                      clickableIcons={false}
                    >
                      <ResetToSavedPinButton pinLocation={pinLocation} />

                      {/* Address pin marker - shows geocoded address location */}
                      {address?.lat && address?.long && (
                        <Marker position={{ lat: address.lat, lng: address.long }} />
                      )}
                      <DumpsterRectangleOverlay
                        position={{ lat: defaultCenter.lat, lng: defaultCenter.lng }}
                        rotation={rotation}
                        lengthMeters={dumpsterLengthMeters}
                        widthMeters={dumpsterWidthMeters}
                        interactive={false}
                      />
                    </Map>
                  </div>

                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <p className="text-xs text-gray-600 text-center">
                      Coordinates: {defaultCenter.lat.toFixed(6)}, {defaultCenter.lng.toFixed(6)} |
                      Rotation: {rotation}°
                    </p>
                  </div>
                </div>

                {/* Send to Driver Button */}
                <>
                  <Button
                    onClick={() => setShowConfirmDialog(true)}
                    className="w-fit self-center"
                    disabled={isSending || !driver}
                  >
                    Send Location to Driver
                  </Button>

                  {driver && (
                    <ConfirmDialog
                      open={showConfirmDialog}
                      onOpenChange={setShowConfirmDialog}
                      title="Send Location to Driver"
                      description={`Send the delivery location to ${driver.name.first} ${driver.name.last} at ${formatPhone(driver.phone)}?`}
                      confirmText="Send Location"
                      onConfirm={handleSendToDriver}
                      isLoading={isSending}
                    />
                  )}
                </>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </APIProvider>
  );
};
